/**
 * Bulk Import — step 1: file picker.
 * Reads the JSON file locally and calls onPreview with the parsed content.
 */

import { useRef, useState } from 'react';
import { Button } from '../../../components/button';
import { useTranslation } from 'react-i18next';
import { FormMessage } from '../../../components/form-field';
import bulk from './bulk-import.module.css';

interface Props {
  onPreview: (parsed: object, fileName: string) => void;
  isLoading: boolean;
}

export function BulkImportUpload({ onPreview, isLoading }: Props) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [parsed, setParsed] = useState<object | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setParseError(null);
    setParsed(null);

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target?.result as string;
        const result = JSON.parse(text);
        setParsed(result);
      } catch {
        setParseError(t('admin.bulkImport.invalidJson'));
      }
    };
    reader.readAsText(file);
  }

  return (
    <div>
      <div className={bulk.bulkImportUploadArea} onClick={() => inputRef.current?.click()}>
        <div className={bulk.bulkImportUploadIcon}>📥</div>
        <p>{fileName ? t('admin.bulkImport.chooseDifferentFile') : t('admin.bulkImport.chooseFile')}</p>
        <p className={bulk.bulkImportUploadHint}>.json</p>
        <input
          ref={inputRef}
          type="file"
          accept="application/json,.json"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </div>

      {fileName && (
        <div className={bulk.bulkImportFileSelected}>
          <span>📄</span>
          <span className={bulk.bulkImportFileName}>{fileName}</span>
        </div>
      )}

      {parseError && (
        <FormMessage>{parseError}</FormMessage>
      )}

      <div className="mt-4 flex gap-2">
        <Button
          type="button"
          variant={parsed && !parseError && !isLoading ? 'primary' : 'gold'}
          disabled={!parsed || !!parseError || isLoading}
          onClick={() => parsed && fileName && onPreview(parsed, fileName)}
        >
          {isLoading ? t('admin.bulkImport.previewing') : t('admin.bulkImport.sendAndPreview')}
        </Button>
      </div>
    </div>
  );
}
